import { useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconArrowDown,
  IconArrowUp,
  IconLink,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  getSidebarIcon,
  SIDEBAR_ICONS,
  type IconGroup,
} from "@/features/space/lib/sidebar-icons";

export interface SidebarLink {
  id: string;
  label: string;
  href: string;
  icon: string;
  external?: boolean;
}

function makeId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// ─────────────────────────────────────────────────────────────────────
// Icon picker modal — searchable grid grouped by Basic / Categories /
// Brands. Returns the picked icon `key` via promise.
// ─────────────────────────────────────────────────────────────────────
function IconPickerForm({
  current,
  modalId,
  resolve,
  allowClear,
}: {
  current: string;
  modalId: string;
  resolve: (val: string | null) => void;
  /** Show a "Remove icon" action that resolves with ''. */
  allowClear?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const groups: { key: IconGroup; label: string }[] = [
    { key: "basic", label: t("Basic") },
    { key: "category", label: t("Categories") },
    { key: "brand", label: t("Brands") },
  ];

  return (
    <Stack gap="md">
      <TextInput
        size="sm"
        placeholder={t("Search icons…")}
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        data-autofocus
      />
      <Box mah={420} style={{ overflowY: "auto" }}>
        {groups.map((g) => {
          const items = SIDEBAR_ICONS.filter(
            (i) =>
              i.group === g.key &&
              (q === "" ||
                i.key.toLowerCase().includes(q) ||
                i.label.toLowerCase().includes(q)),
          );
          if (items.length === 0) return null;
          return (
            <Box key={g.key} mb="md">
              <Text size="xs" c="dimmed" tt="uppercase" mb={6}>
                {g.label}
              </Text>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
                  gap: 4,
                }}
              >
                {items.map((it) => {
                  const Cmp = it.component;
                  const active = it.key === current;
                  return (
                    <Tooltip key={it.key} label={it.label} withArrow>
                      <ActionIcon
                        size="xl"
                        variant={active ? "filled" : "default"}
                        onClick={() => {
                          resolve(it.key);
                          modals.close(modalId);
                        }}
                        aria-label={it.label}
                      >
                        <Cmp size={20} />
                      </ActionIcon>
                    </Tooltip>
                  );
                })}
              </div>
            </Box>
          );
        })}
      </Box>
      <Group justify={allowClear ? "space-between" : "flex-end"}>
        {allowClear ? (
          <Button
            variant="subtle"
            color="red"
            size="xs"
            onClick={() => {
              resolve("");
              modals.close(modalId);
            }}
          >
            {t("Remove icon")}
          </Button>
        ) : null}
        <Button
          variant="default"
          size="xs"
          onClick={() => {
            resolve(null);
            modals.close(modalId);
          }}
        >
          {t("Cancel")}
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * Open the curated-library icon picker. Resolves with the picked key,
 * '' when the user chose "Remove icon" (only offered with `allowClear`),
 * or null when the dialog was cancelled/closed.
 *
 * Shared beyond sidebar links: the space tree uses it to assign icons
 * to group headers.
 */
export function openIconPicker(
  current: string,
  opts?: { allowClear?: boolean },
): Promise<string | null> {
  return new Promise((resolve) => {
    const modalId = `icon-picker-${makeId()}`;
    let settled = false;
    const settle = (v: string | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    modals.open({
      modalId,
      title: "Pick icon",
      size: "md",
      withCloseButton: true,
      closeOnEscape: true,
      closeOnClickOutside: true,
      onClose: () => settle(null),
      children: (
        <IconPickerForm
          current={current}
          modalId={modalId}
          resolve={settle}
          allowClear={opts?.allowClear}
        />
      ),
    });
  });
}

// ─────────────────────────────────────────────────────────────────────
// SidebarLinksEditor — list with add/edit/remove/reorder controls.
// Parent passes the current array + change handler; saving is folded
// into the surrounding space-public-settings "Save" button.
// ─────────────────────────────────────────────────────────────────────
export function SidebarLinksEditor({
  value,
  onChange,
}: {
  value: SidebarLink[];
  onChange: (next: SidebarLink[]) => void;
}) {
  const { t } = useTranslation();

  const update = (id: string, patch: Partial<SidebarLink>) =>
    onChange(value.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const remove = (id: string) =>
    onChange(value.filter((l) => l.id !== id));

  const move = (id: string, dir: -1 | 1) => {
    const idx = value.findIndex((l) => l.id === id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= value.length) return;
    const next = value.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };

  const add = () =>
    onChange([
      ...value,
      {
        id: makeId(),
        label: "",
        href: "",
        icon: "link",
        external: false,
      },
    ]);

  const pickIcon = async (id: string, current: string) => {
    const next = await openIconPicker(current);
    if (next) update(id, { icon: next });
  };

  return (
    <Stack gap="sm">
      {value.length === 0 ? (
        <Text size="xs" c="dimmed">
          {t(
            "No pinned links yet. Add a link to show it above the page tree on the public sidebar.",
          )}
        </Text>
      ) : (
        value.map((link, i) => {
          const Icon = getSidebarIcon(link.icon);
          return (
            <Paper key={link.id} withBorder p="sm">
              <Group wrap="nowrap" align="flex-start" gap="sm">
                <Stack gap={4} align="center" style={{ flexShrink: 0 }}>
                  <Tooltip label={t("Pick icon")} withArrow>
                    <ActionIcon
                      size="lg"
                      variant="default"
                      onClick={() => pickIcon(link.id, link.icon)}
                      aria-label={t("Pick icon")}
                    >
                      <Icon size={20} />
                    </ActionIcon>
                  </Tooltip>
                  <Group gap={2}>
                    <Tooltip label={t("Move up")} withArrow>
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        disabled={i === 0}
                        onClick={() => move(link.id, -1)}
                        aria-label={t("Move up")}
                      >
                        <IconArrowUp size={12} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("Move down")} withArrow>
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        disabled={i === value.length - 1}
                        onClick={() => move(link.id, 1)}
                        aria-label={t("Move down")}
                      >
                        <IconArrowDown size={12} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Stack>

                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                  <TextInput
                    size="xs"
                    label={t("Label")}
                    placeholder="Overview"
                    value={link.label}
                    onChange={(e) =>
                      update(link.id, { label: e.currentTarget.value })
                    }
                  />
                  <TextInput
                    size="xs"
                    label={t("URL")}
                    placeholder="/start or https://…"
                    value={link.href}
                    onChange={(e) =>
                      update(link.id, { href: e.currentTarget.value })
                    }
                  />
                  <Switch
                    size="xs"
                    label={t("Open in new tab")}
                    checked={link.external === true}
                    onChange={(e) =>
                      update(link.id, { external: e.currentTarget.checked })
                    }
                  />
                </Stack>

                <Tooltip label={t("Remove")} withArrow>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => remove(link.id)}
                    aria-label={t("Remove")}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Paper>
          );
        })
      )}

      <Button
        size="xs"
        variant="default"
        leftSection={<IconPlus size={14} />}
        onClick={add}
      >
        {t("Add link")}
      </Button>
    </Stack>
  );
}

// Quiet IconLink unused-import warning if extension trees drift.
void IconLink;
